"use client";

import { BlurFade } from "@/components/ui/blur-fade";
import { Check, Star, Trophy, BadgeDollarSign } from "lucide-react";

const ICON_MAP = {
  Star,
  Trophy,
};

interface Plan {
  id: "monthly" | "annual" | "biannual";
  name: string;
  icon: string;
  description: string;
  price: {
    monthly: number | null;
    annual: number | null;
    biannual: number | null;
  };
  priceLabel: {
    monthly: string | null;
    annual: string | null;
    biannual: string | null;
  };
  billingNote: string;
  savingsBadge?: string | null;
  popular: boolean;
  freeTrial?: boolean;
  features: string[];
}

interface PricingCardsProps {
  plans: Plan[];
  guaranteeBanner: {
    title: string;
    description: string;
  };
}

const PricingCards = ({ plans = [], guaranteeBanner }: PricingCardsProps) => {
  if (!plans || plans.length === 0) {
    return null;
  }

  return (
    <section className="pt-4 pb-12 md:pt-8 md:pb-12 lg:pt-10 lg:pb-24 bg-gradient-to-b from-white to-secondary/20">
      <div className="container max-w-7xl mx-auto px-4 lg:px-6">
        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 lg:gap-8 max-w-6xl mx-auto">
          {plans.map((plan, index) => {
            const Icon = ICON_MAP[plan.icon as keyof typeof ICON_MAP] || Star;
            const isActive = plan.popular;
            const priceLabel = plan.priceLabel?.[plan.id as keyof typeof plan.priceLabel] || "";

            return (
              <BlurFade key={plan.id} delay={0.2 + index * 0.1} className="md:h-full">
                <div
                  className={`relative md:h-full md:flex md:flex-col bg-white rounded-2xl lg:rounded-3xl p-4 md:p-6 lg:p-8 transition-all duration-300 ${
                    isActive
                      ? "ring-2 ring-primary shadow-2xl shadow-primary/20"
                      : "border border-border shadow-sm"
                  }`}
                >
                  {/* Popular Badge */}
                  {plan.popular && isActive && (
                    <div className="absolute -top-3 lg:-top-4 left-1/2 -translate-x-1/2 bg-primary text-white text-[10px] sm:text-xs font-black px-3 sm:px-4 py-1 sm:py-1.5 rounded-full uppercase tracking-wider">
                      Most Popular
                    </div>
                  )}

                  {/* Savings Badge - Desktop only (positioned absolute) */}
                  {plan.savingsBadge && (
                    <div className="hidden md:block absolute top-4 right-4 bg-primary text-white text-[10px] font-black px-2.5 py-1 rounded-full whitespace-nowrap">
                      {plan.savingsBadge}
                    </div>
                  )}

                  {/* Mobile Layout */}
                  <div className="md:hidden">
                    {/* Row 1: Name + Price */}
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <h3 className="text-lg font-bold text-foreground">
                          {plan.name}
                        </h3>
                      </div>
                      <div className="flex flex-col items-end flex-shrink-0">
                        <span className="text-lg font-bold text-foreground leading-none">
                          {priceLabel}
                        </span>
                        {plan.savingsBadge && (
                          <div className="mt-1 bg-primary text-white text-[10px] font-black px-2 py-0.5 rounded-full whitespace-nowrap">
                            {plan.savingsBadge}
                          </div>
                        )}
                      </div>
                    </div>
                    {/* Billing note */}
                    <p className="text-[11px] text-foreground/60 mt-2">
                      {plan.billingNote}
                    </p>
                    {/* Free trial badge */}
                    {plan.freeTrial && (
                      <div className="mt-2 inline-flex items-center gap-1.5 bg-primary/10 text-primary px-2 py-1 rounded-lg">
                        <Check className="w-3 h-3 flex-shrink-0" />
                        <span className="text-[10px] font-bold">7 day free trial included</span>
                      </div>
                    )}
                  </div>

                  {/* Desktop Layout */}
                  <div className="hidden md:block">
                    {/* Plan Header */}
                    <div className="flex items-center gap-3 mb-4">
                      <div
                        className={`w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 ${
                          isActive ? "bg-primary/10" : "bg-secondary"
                        }`}
                      >
                        <Icon
                          className={`w-6 h-6 ${isActive ? "text-primary" : "text-foreground/60"}`}
                        />
                      </div>
                      <div className="min-w-0">
                        <h3 className="text-lg lg:text-xl font-bold text-foreground truncate">
                          {plan.name}
                        </h3>
                        <p className="text-xs text-foreground/60 truncate">
                          {plan.description}
                        </p>
                      </div>
                    </div>

                    {/* Price */}
                    <div className="mb-6 mt-6 lg:mt-8">
                      <div className="flex items-baseline gap-2">
                        <span className="text-3xl lg:text-4xl font-bold text-foreground leading-none">
                          {priceLabel}
                        </span>
                      </div>
                      <p className="text-xs text-foreground/60 mt-2 line-clamp-2">
                        {plan.billingNote}
                      </p>
                      {plan.freeTrial && (
                        <div className="mt-3 inline-flex items-center gap-2 bg-primary/10 text-primary px-3 py-1.5 rounded-lg">
                          <Check className="w-4 h-4 flex-shrink-0" />
                          <span className="text-xs font-bold">7 day free trial included</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* CTA Button */}
                  <button
                    className={`w-full mt-4 md:mt-0 inline-flex items-center justify-center gap-2 font-bold rounded-xl md:rounded-2xl px-6 md:px-8 py-3 md:py-4 transition-all duration-200 ${
                      isActive
                        ? "bg-primary text-white shadow-xl shadow-primary/20 hover:bg-primary/90 hover:shadow-2xl hover:scale-[1.02]"
                        : "bg-white border-2 border-foreground text-foreground hover:bg-foreground hover:text-white"
                    }`}
                  >
                    Coming Soon
                  </button>
                  <div className="hidden md:flex items-center justify-center gap-2 mt-5 mb-4 lg:mb-6">
                    <BadgeDollarSign className="w-5 h-5 text-foreground flex-shrink-0" />
                    <span className="text-xs md:text-sm font-bold text-foreground">
                      30-day money-back guarantee
                    </span>
                  </div>

                  {/* Features - Hidden on mobile */}
                  <div className="hidden md:block space-y-3 lg:space-y-4 flex-grow">
                    <p className="text-xs font-bold text-foreground/50 uppercase tracking-wider">
                      What&apos;s included:
                    </p>
                    <ul className="space-y-2.5 lg:space-y-3">
                      {plan.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-3">
                          <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                          <span className="text-sm text-foreground/80 leading-tight">
                            {feature}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </BlurFade>
            );
          })}
        </div>

        {/* Money-back Guarantee */}
        <BlurFade delay={0.6}>
          <div className="mt-8 lg:mt-16 text-center px-4">
            <div className="inline-flex items-center gap-2 sm:gap-3 bg-white px-4 sm:px-6 lg:px-8 py-3 sm:py-4 rounded-xl lg:rounded-2xl shadow-sm border border-border max-w-lg mx-auto">
              <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Check className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
              </div>
              <div className="text-left">
                <p className="font-bold text-foreground text-sm sm:text-base">
                  {guaranteeBanner.title}
                </p>
                <p className="text-xs sm:text-sm text-foreground/60">
                  {guaranteeBanner.description}
                </p>
              </div>
            </div>
          </div>
        </BlurFade>
      </div>
    </section>
  );
};

export default PricingCards;
